import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { notifyAuthStateChanged } from '../utils/authSession';

/**
 * Custom hook for session timeout management
 * 
 * Features:
 * - Tracks user activity (mouse, keyboard, touch, scroll)
 * - Shows warning modal 60 seconds before logout
 * - Auto-logout after inactivity period
 * - Integrates with existing JWT token system
 * 
 * Configuration:
 * - INACTIVITY_LIMIT: 15 minutes (900,000 ms)
 * - WARNING_TIME: 14 minutes (840,000 ms)
 * - COUNTDOWN_DURATION: 60 seconds
 */

const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes in milliseconds
const WARNING_TIME = 14 * 60 * 1000; // 14 minutes in milliseconds
const COUNTDOWN_DURATION = 60; // 60 seconds countdown

export function useSessionTimeout() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION);
  const [showLogoutToast, setShowLogoutToast] = useState(false);
  const [logoutMessage, setLogoutMessage] = useState('');
  
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Check if user is authenticated
  const isAuthenticated = useCallback(() => {
    return !!localStorage.getItem('spes_token');
  }, []);

  // Clear all tokens and user data
  const clearSession = useCallback(() => {
    localStorage.removeItem('spes_token');
    localStorage.removeItem('spes_role');
    localStorage.removeItem('spes_user');
    notifyAuthStateChanged();
    console.log('🔒 Session cleared');
  }, []);

  // Handle logout
  const handleLogout = useCallback((message = '🔒 Session expired due to inactivity.') => {
    console.log('🔒 Auto-logout triggered:', message);
    
    // Clear timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Close modal if open
    setShowWarningModal(false);
    
    // Clear session
    clearSession();
    
    // Show toast message
    setLogoutMessage(message);
    setShowLogoutToast(true);
    
    // Redirect to login after brief delay
    setTimeout(() => {
      setShowLogoutToast(false);
      navigate('/login', { replace: true });
    }, 3000);
  }, [navigate, clearSession]);

  // Reset all timers
  const resetTimers = useCallback(() => {
    // Clear existing timers
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    
    // Update last activity
    lastActivityRef.current = Date.now();
    
    // Close warning modal if open
    if (showWarningModal) {
      setShowWarningModal(false);
      setCountdown(COUNTDOWN_DURATION);
    }
    
    // Only set timers if user is authenticated and not on login page
    if (isAuthenticated() && location.pathname !== '/login') {
      // Set warning timer (14 minutes)
      warningTimerRef.current = setTimeout(() => {
        console.log('⚠️ Warning: Session about to expire');
        setShowWarningModal(true);
        setCountdown(COUNTDOWN_DURATION);
        
        // Start countdown
        countdownIntervalRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, WARNING_TIME);
      
      // Set inactivity timer (15 minutes)
      inactivityTimerRef.current = setTimeout(() => {
        handleLogout('🔒 Session expired due to inactivity.');
      }, INACTIVITY_LIMIT);
    }
  }, [showWarningModal, isAuthenticated, location.pathname, handleLogout]);

  // Handle user activity
  const handleActivity = useCallback(() => {
    // Only track if authenticated and not on login page
    if (isAuthenticated() && location.pathname !== '/login') {
      resetTimers();
    }
  }, [isAuthenticated, location.pathname, resetTimers]);

  // Handle "Stay Logged In" button
  const handleStayLoggedIn = useCallback(() => {
    console.log('✅ User chose to stay logged in');
    resetTimers();
  }, [resetTimers]);

  // Handle "Logout Now" button
  const handleLogoutNow = useCallback(() => {
    console.log('👋 User manually logged out from warning modal');
    handleLogout('👋 You have been logged out.');
  }, [handleLogout]);

  // Set up activity listeners
  useEffect(() => {
    // Only set up if authenticated and not on login page
    if (!isAuthenticated() || location.pathname === '/login') {
      return;
    }

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    
    // Add event listeners
    events.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Initialize timers
    resetTimers();

    // Cleanup
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isAuthenticated, location.pathname, handleActivity, resetTimers]);

  // Listen for storage changes (logout from another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'spes_token' && !e.newValue) {
        // Token was removed (logout from another tab)
        console.log('🔒 Logout detected from another tab');
        handleLogout('🔒 You have been logged out.');
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleLogout]);

  return {
    showWarningModal,
    countdown,
    showLogoutToast,
    logoutMessage,
    handleStayLoggedIn,
    handleLogoutNow,
  };
}
